/**
 * Firm Data Service - Unified data fetching for firm branding
 * Primary source: Backend API (/api/firms/branding/all)
 * Fallback: Static FIRM_BRANDING from formatFirmDisplay.js (visual-only: logos, colors)
 */
import { FIRM_BRANDING } from './formatFirmDisplay';
import { fetchWithRetry } from './fetchRetry';
import { API } from '@/utils/constants';
import { logger } from "./logger";

let cachedBranding = null;
let lastFetchTime = 0;
const CACHE_TTL = 5 * 60 * 1000;

export async function fetchBrandingData() {
  const now = Date.now();
  if (cachedBranding && (now - lastFetchTime) < CACHE_TTL) {
    return { branding: cachedBranding };
  }
  try {
    const response = await fetchWithRetry(`${API}/firms/branding/all`);
    if (!response.ok) throw new Error('Failed to fetch branding data');
    const data = await response.json();
    cachedBranding = transformBrandingData(data.branding || {});
    lastFetchTime = now;
    return { branding: cachedBranding };
  } catch (error) {
    logger.warn('Failed to fetch branding from API, using visual fallback');
    return { branding: FIRM_BRANDING };
  }
}

function transformBrandingData(apiBranding) {
  const transformed = {};
  for (const [slug, data] of Object.entries(apiBranding)) {
    if (!data || Object.keys(data).length === 0) {
      transformed[slug] = FIRM_BRANDING[slug] || {};
      continue;
    }
    transformed[slug] = {
      logo: data.logo,
      logoAlt: data.logo_alt,
      brandColor: data.brand_color,
      textColor: data.text_color,
      logoBgLight: data.logo_bg_light,
    };
  }
  return { ...FIRM_BRANDING, ...transformed };
}

export function getBranding(slug) {
  if (cachedBranding && cachedBranding[slug]) {
    return cachedBranding[slug];
  }
  return FIRM_BRANDING[slug] || {};
}

export async function initializeFirmData() {
  try {
    await fetchBrandingData();
  } catch (err) {
    logger.error('[firmDataService] Failed to initialize firm data:', err);
    // Visual fallback already available via FIRM_BRANDING
  }
}
